import React, { useMemo, useState } from 'react';
import { Button, Dropdown, Form } from 'react-bootstrap';
import { MoreVertical, Search, Star, Pen, Trash, ChevronDown, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import { makeRESTApiRequest } from '../../helpers/api';
import { ENDPOINTS } from '../../helpers/constants';
import SaveEditFilterModel from './SaveEditFilterModel';
import AlertModal from '../AlertModal';
import Spinner from 'react-bootstrap/Spinner';

const VISIBLE_VIEWS_COUNT = 4;

const partitionSavedFilters = (savedFilters, selectedFilterId) => {
  if (savedFilters.length <= VISIBLE_VIEWS_COUNT) {
    return { visibleFilters: savedFilters, overflowFilters: [] };
  }

  const selectedIndex = savedFilters.findIndex(filter => filter._id === selectedFilterId);

  if (selectedIndex >= VISIBLE_VIEWS_COUNT) {
    const selectedFilter = savedFilters[selectedIndex];
    const withoutSelected = savedFilters.filter(filter => filter._id !== selectedFilterId);
    const visibleFilters = [...withoutSelected.slice(0, VISIBLE_VIEWS_COUNT - 1), selectedFilter];
    const visibleIds = new Set(visibleFilters.map(filter => filter._id));
    const overflowFilters = savedFilters.filter(filter => !visibleIds.has(filter._id));
    return { visibleFilters, overflowFilters };
  }

  return {
    visibleFilters: savedFilters.slice(0, VISIBLE_VIEWS_COUNT),
    overflowFilters: savedFilters.slice(VISIBLE_VIEWS_COUNT)
  };
};

const SavedFiltersList = ({
  savedFilters,
  selectedFilterId,
  onFilterSelect,
  setSavedFilters,
  handleClearViewFilter
}) => {
  const [editModalMetaData, setEditModalMetaData] = useState({ isOpen: false, filter: null, isUpdating: false });
  const [deleteModalMetaData, setDeleteModalMetaData] = useState({ isOpen: false, filterId: null, isDeleting: false });
  const [setAsDefaultFilterId, setSetAsDefaultFilterId] = useState(null);
  const [openDropdownId, setOpenDropdownId] = useState(null);
  const [showOverflowDropdown, setShowOverflowDropdown] = useState(false);
  const [overflowSearch, setOverflowSearch] = useState('');

  const { visibleFilters, overflowFilters } = useMemo(() => partitionSavedFilters(savedFilters, selectedFilterId), [
    savedFilters,
    selectedFilterId
  ]);

  const filteredOverflowFilters = useMemo(() => {
    const query = overflowSearch.trim().toLowerCase();
    if (!query) return overflowFilters;
    return overflowFilters.filter(filter => filter.name.toLowerCase().includes(query));
  }, [overflowFilters, overflowSearch]);

  const closeModal = () => setEditModalMetaData({ isOpen: false, filter: null });
  const closeDeleteModal = () => setDeleteModalMetaData({ isOpen: false, filterId: null, isDeleting: false });
  const handleDropdownToggle = (filterId, isOpen) => setOpenDropdownId(isOpen ? filterId : null);

  const handleOverflowDropdownToggle = isOpen => {
    setShowOverflowDropdown(isOpen);
    if (!isOpen) setOverflowSearch('');
  };

  const dropdownItemStyle = {
    padding: '8px 16px',
    fontSize: '14px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  };

  const handleRename = filter => {
    setOpenDropdownId(null);
    setEditModalMetaData({ isOpen: true, filter });
  };

  const handleEditSave = async ({ viewName }) => {
    if (!editModalMetaData.filter) return;

    setEditModalMetaData(prev => ({ ...prev, isUpdating: true }));
    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.UPDATE_SAVED_FILTER(editModalMetaData.filter._id),
      method: 'PUT',
      requestBody: { name: viewName }
    });

    setEditModalMetaData(prev => ({ ...prev, isUpdating: false }));
    if (error) {
      toast.error(error || 'Error while updating filter view.');
      return;
    }

    toast.success('Filter view updated successfully.');
    setSavedFilters(prevFilters =>
      prevFilters
        .map(filter => (filter._id === editModalMetaData.filter._id ? { ...filter, name: viewName } : filter))
        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
    );
    closeModal();
  };

  const handleDeleteClick = filterId => {
    setOpenDropdownId(null);
    setDeleteModalMetaData({ isOpen: true, filterId, isDeleting: false });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteModalMetaData.filterId) return;

    setDeleteModalMetaData(prev => ({ ...prev, isDeleting: true }));

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.DELETE_SAVED_FILTER(deleteModalMetaData.filterId),
      method: 'DELETE'
    });

    if (error) {
      toast.error(error || 'Error while deleting filter view.');
      closeDeleteModal();
      return;
    }

    toast.success('Filter view deleted successfully.');
    setSavedFilters(prevFilters =>
      prevFilters
        .filter(filter => filter._id !== deleteModalMetaData.filterId)
        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
    );
    closeDeleteModal();
    handleClearViewFilter && handleClearViewFilter();
  };

  const handleSetAsDefaultOrRemoveDefault = async (filter, isSetAsDefault) => {
    setOpenDropdownId(null);
    setSetAsDefaultFilterId(filter._id);

    const { error } = await makeRESTApiRequest({
      endpoint: ENDPOINTS.UPDATE_SAVED_FILTER(filter._id),
      method: 'PUT',
      requestBody: { isDefault: isSetAsDefault }
    });

    if (error) {
      toast.error(error || 'Error while setting default filter view.');
      setSetAsDefaultFilterId(null);
      return;
    }

    toast.success('Default filter view updated successfully.');

    const updatedFilter = { ...filter, isDefault: isSetAsDefault };

    // Auto-select the filter when filter is set as default
    if (isSetAsDefault) {
      onFilterSelect(updatedFilter);
    }
    setSavedFilters(prevFilters =>
      prevFilters
        .map(f => (f._id === filter._id ? updatedFilter : { ...f, isDefault: false }))
        .sort((a, b) => (b.isDefault ? 1 : 0) - (a.isDefault ? 1 : 0))
    );

    setSetAsDefaultFilterId(null);
  };

  const handleOverflowFilterSelect = filter => {
    onFilterSelect(filter);
    setShowOverflowDropdown(false);
    setOverflowSearch('');
  };

  const renderFilterChip = filter => {
    const isSelected = selectedFilterId === filter._id;
    return (
      <div key={filter._id} className="position-relative">
        <Button
          variant={isSelected ? 'primary' : 'outline-secondary'}
          size="sm"
          disabled={setAsDefaultFilterId === filter._id}
          onClick={() => onFilterSelect(filter)}
          title={filter.name}
          style={{
            padding: '6px 32px 6px 12px',
            borderRadius: '6px',
            border: isSelected ? '1px solid #FFC72C' : '1px solid #D0D0D0',
            display: 'flex',
            alignItems: 'center',
            gap: '6px',
            fontSize: '14px',
            backgroundColor: isSelected ? '#FFC72C' : '#ffffff',
            color: isSelected ? '#ffffff' : '#4A4A4A',
            fontWeight: isSelected ? 600 : 400,
            position: 'relative',
            transition: 'background-color 0.2s ease-in-out, color 0.2s ease-in-out, border-color 0.2s ease-in-out',
            boxShadow: isSelected ? '0 2px 4px rgba(255, 199, 44, 0.2)' : 'none',
            maxWidth: '150px',
          }}
        >
          {filter.isDefault && (
            <Star size={14} fill={isSelected ? '#ffffff' : '#FACC15'} color={isSelected ? '#ffffff' : '#FACC15'} className="flex-shrink-0" />
          )}
          <span className="text-truncate" style={{ flex: 1, minWidth: 0 }}>{filter.name}</span>
          {setAsDefaultFilterId === filter._id && (
            <Spinner
              size="sm"
              animation="border"
              variant="dark"
              className="flex-shrink-0"
              style={{ width: '14px', height: '14px', borderWidth: '2px' }}
            />
          )}
        </Button>
        <Dropdown
          onClick={e => e.stopPropagation()}
          show={openDropdownId === filter._id}
          onToggle={isOpen => handleDropdownToggle(filter._id, isOpen)}
          style={{ position: 'absolute', right: '4px', top: '50%', transform: 'translateY(-50%)', zIndex: '99' }}
        >
          <Dropdown.Toggle
            as="button"
            style={{
              padding: '2px',
              border: 'none',
              background: 'transparent',
              color: isSelected ? '#000000' : '#6c757d',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              transition: 'background-color 0.2s',
              width: '20px',
              height: '20px',
              lineHeight: 1
            }}
            className="dropdown-toggle-no-arrow"
            onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = isSelected ? 'rgba(0, 0, 0, 0.1)' : '#f1f1f1';
            }}
            onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = 'transparent';
            }}
          >
            <MoreVertical size={16} color={isSelected ? '#ffffff' : '#6c757d'} />
          </Dropdown.Toggle>
          <Dropdown.Menu
            align="end"
            style={{
              minWidth: '180px',
              zIndex: 1050,
              marginTop: '10px',
              marginRight: '-6px'
            }}
          >
            <Dropdown.Item onClick={() => handleRename(filter)} style={dropdownItemStyle}>
              <Pen size={14} />
              Rename
            </Dropdown.Item>
            <Dropdown.Item
              onClick={() => handleSetAsDefaultOrRemoveDefault(filter, !filter.isDefault)}
              style={dropdownItemStyle}
            >
              <Star size={14} />
              {filter.isDefault ? 'Remove Default' : 'Set As Default'}
            </Dropdown.Item>
            <Dropdown.Divider style={{ margin: '4px 0' }} />
            <Dropdown.Item
              onClick={() => handleDeleteClick(filter._id)}
              className="text-danger"
              style={dropdownItemStyle}
            >
              <Trash size={14} />
              Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    );
  };

  return (
    <>
      <div className="saved-filters-list d-flex align-items-center flex-wrap" style={{ gap: '8px' }}>
        {visibleFilters.map(renderFilterChip)}
        {overflowFilters.length > 0 && (
          <Dropdown show={showOverflowDropdown} onToggle={handleOverflowDropdownToggle}>
            <Dropdown.Toggle
              as={Button}
              variant="outline-secondary"
              size="sm"
              className="dropdown-toggle-no-arrow saved-filters-list__overflow-toggle"
              style={{
                padding: '6px 10px 6px 12px',
                borderRadius: '6px',
                border: `1px solid ${showOverflowDropdown ? '#FACC15' : '#e0c97a'}`,
                fontSize: '13px',
                backgroundColor: showOverflowDropdown ? '#FFF8E1' : '#FFFBEC',
                color: '#7C5800',
                fontWeight: 600,
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                boxShadow: showOverflowDropdown
                  ? '0 0 0 3px rgba(250,204,21,0.18)'
                  : '0 1px 2px rgba(0,0,0,0.06)',
                transition: 'background-color 0.15s, border-color 0.15s, box-shadow 0.15s'
              }}
            >
              <LayoutList size={13} style={{ flexShrink: 0, opacity: 0.85 }} />
              +{overflowFilters.length} more views
              <ChevronDown
                size={13}
                style={{
                  flexShrink: 0,
                  transform: showOverflowDropdown ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s ease'
                }}
              />
            </Dropdown.Toggle>
            <Dropdown.Menu
              className="saved-filters-list__overflow-menu"
              style={{ minWidth: '240px', zIndex: 1050, padding: '8px' }}
            >
              <div
                className="position-relative mb-2"
                onClick={e => e.stopPropagation()}
                onKeyDown={e => e.stopPropagation()}
              >
                <Search
                  size={14}
                  className="position-absolute"
                  style={{
                    left: 10,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    color: '#6c757d',
                    pointerEvents: 'none'
                  }}
                />
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Search views..."
                  value={overflowSearch}
                  onChange={e => setOverflowSearch(e.target.value)}
                  onClick={e => e.stopPropagation()}
                  onKeyDown={e => e.stopPropagation()}
                  style={{ paddingLeft: 30 }}
                />
              </div>
              <div className="saved-filters-list__overflow-items">
                {filteredOverflowFilters.length > 0 ? (
                  filteredOverflowFilters.map(filter => {
                    const isSelected = selectedFilterId === filter._id;
                    return (
                      <Dropdown.Item
                        key={filter._id}
                        active={isSelected}
                        onClick={() => handleOverflowFilterSelect(filter)}
                        style={{
                          ...dropdownItemStyle,
                          fontWeight: isSelected ? 600 : 400
                        }}
                      >
                        {filter.isDefault && (
                          <Star size={14} fill="#FACC15" color="#FACC15" className="flex-shrink-0" />
                        )}
                        <span className="text-truncate">{filter.name}</span>
                      </Dropdown.Item>
                    );
                  })
                ) : (
                  <div className="text-muted small px-2 py-1">No views found</div>
                )}
              </div>
            </Dropdown.Menu>
          </Dropdown>
        )}
      </div>

      <SaveEditFilterModel
        mode="edit"
        show={editModalMetaData.isOpen}
        onHide={closeModal}
        onSave={handleEditSave}
        defaultValue={editModalMetaData.filter}
        isUpdating={editModalMetaData.isUpdating}
      />

      <AlertModal
        show={deleteModalMetaData.isOpen}
        onHide={closeDeleteModal}
        onDismissClick={closeDeleteModal}
        onContinueClick={handleDeleteConfirm}
        showProgress={deleteModalMetaData.isDeleting}
        alertText="Are you sure you want to delete this filter view? This action cannot be undone!"
        progressText="Deleting filter view..."
      />
    </>
  );
};

export default SavedFiltersList;
